/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useState } from 'react'
import { KTSVG, toAbsoluteUrl } from '../../../helpers'
import { Rating } from '../../content/Rating'
import { LanguageHelp } from '../../../helpers/LanguageHelper'
import { Link } from 'react-router-dom'
import { getHappinessScore } from '../../../helpers/utility'
import { useThemeMode } from '../../../../_metronic/partials/layout/theme-mode/ThemeModeProvider'

type Props = {
  uniqueKey: string
  tableName?: string
  className: string
  chartSize?: number
  datas?: TablesWidget15Dto
  texts?: string[]
  firstColor?: string
  secondColor?: string
  thirdColor?: string
  fourthColor?: string
  mainLabel?: string
  sideLabel?: string
  functionDownload?: any
  isDownloadContinue?: boolean
  url?: string
  warnings: { url: string, message: string }[]
  isWaringImageMirror?: boolean

  columnCount?: number
  header1: string
  header2: string
  header3: string
  header4: string
  header5: string
  header6: string

  filter_Date: string
  filter_ChannelType: string
  filter_SourceType: string
}

export class TablesWidget15ItemDto {
  id = "";
  label = "";
  competitor = "";
  firstCount = 0;
  secondCount = 0;
  thirdCount = 0;
  fourthCount = 0;
  fifthCount = 0;
  sixthCount = 0;
  firstRate = 0;
  secondRate = 0;
  thirdRate = 0;
  fourthRate = 0;
  fifthRate = 0;
  sixthRate = 0
}

export class TablesWidget15Dto {
  items: TablesWidget15ItemDto[] = [new TablesWidget15ItemDto()]
  totalCount = 0
  totalRate = 0
}

const TablesWidget15: React.FC<Props> = ({
  uniqueKey,
  tableName = "",
  className,
  chartSize = 90,
  datas = new TablesWidget15Dto(),
  texts = ["Net Profit", "Net Profit"],
  mainLabel = "Your Statistics",
  sideLabel = "",
  functionDownload,
  isDownloadContinue = false,
  url = "",
  warnings = [],
  isWaringImageMirror = false,

  columnCount = 4,
  header1 = LanguageHelp("Total"),
  header2 = LanguageHelp("Positive"),
  header3 = LanguageHelp("Negative"),
  header4 = LanguageHelp("Neutral"),
  header5 = "",
  header6 = "",

  filter_Date = "",
  filter_ChannelType = "",
  filter_SourceType = "",
}) => {
  const { mode } = useThemeMode()
  const [currentItems, setCurrentItems] = useState([new TablesWidget15ItemDto()])
  const [sorting, setSorting] = useState({ key: 'totalCount', ascending: false })

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(100)

  const totalPages = Math.ceil(currentItems.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedItems = currentItems.slice(startIndex, endIndex)

  function applySorting(key: string) {
    let ascending = false
    if (key === sorting.key) {
      ascending = !sorting.ascending
    }
    setSorting({ key: key, ascending: ascending })

    const currentItemsCopy = [...datas.items]
    if (key === "label") {
      setCurrentItems(currentItemsCopy.sort((a, b) => sortByText(a, b, ascending)))
    } else if (key.indexOf("Count") > 0) {
      setCurrentItems(currentItemsCopy.sort((a, b) => sortByNum(a, b, ascending, key)))
    }

    setCurrentPage(1)
  }

  function sortByNum(a: any, b: any, ascending: boolean, column: string) {
    let diff = 0;

    if (column === "firstCount") { diff = a.firstCount - b.firstCount }
    if (column === "secondCount") { diff = a.secondCount - b.secondCount }
    if (column === "thirdCount") { diff = a.thirdCount - b.thirdCount }
    if (column === "fourthCount") { diff = a.fourthCount - b.fourthCount }
    if (column === "fifthCount") { diff = a.fifthCount - b.fifthCount }
    if (column === "sixthCount") { diff = a.sixthCount - b.sixthCount }

    if (ascending === true) { return diff; }
    return -1 * diff;
  }

  function sortByText(a: any, b: any, ascending: boolean) {
    const diff = a.label.toLowerCase().localeCompare(b.label.toLowerCase());
    if (ascending === true) { return diff; }
    return -1 * diff;
  }

  useEffect(() => {
    setSorting({ key: "firstCount", ascending: false });
    setCurrentItems(datas.items)
    setCurrentPage(1)
  }, [datas]);

  return (
    <div className={`card ${className} minh-md-600px`} key={uniqueKey}>
      {/* begin::Header */}
      <div className='card-header pt-5'>
        <div className='card-title align-items-start flex-column'>
          <span className='card-label fw-bold text-dark'>{mainLabel}</span>
          {sideLabel !== "" &&
            <div>
              <span className='text-gray-400 fw-semibold fs-6'>{sideLabel}:</span>
              {datas.totalCount !== 0 &&
                <div style={{ display: 'unset' }}>
                  <span className='card-label fw-bolder text-muted text-gray-700 mt-2 pt-1'>{datas.totalCount}</span>
                  <Rating rate={datas.totalRate} />
                </div>
              }
            </div>
          }
        </div>
        <div className='card-toolbar'>
          {/* Pagination Controls */}
          <div className='d-flex justify-content-between align-items-center mb-2'>
            {/* Previous / Next */}
            <div className='me-4'>
              <button
                className='btn btn-sm btn-light me-2'
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
              >
                ‹
              </button>
              <button
                className='btn btn-sm btn-light'
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                ›
              </button>
            </div>

            {/* Page Info */}
            <span className='fw-semibold me-4'>
              {LanguageHelp('Page')} {currentPage} / {totalPages}
            </span>

            {/* Page Size Selector */}
            <div className='d-flex align-items-center me-4'>
              <select
                className='form-select form-select-sm'
                value={itemsPerPage === currentItems.length ? LanguageHelp('All') : itemsPerPage}
                onChange={(e) => {
                  const value = e.target.value
                  if (value === LanguageHelp('All')) {
                    setItemsPerPage(currentItems.length)
                    setCurrentPage(1)
                  } else {
                    setItemsPerPage(parseInt(value))
                    setCurrentPage(1)
                  }
                }}
              >
                {[30, 100, 200, LanguageHelp('All')].map((v, idx) => (
                  <option key={idx} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <button
            type='button'
            className='btn btn-sm btn-icon btn-bg-light btn-active-color-primary mb-2'
            data-kt-menu-trigger='click'
            data-kt-menu-placement='bottom-end'
            data-kt-menu-flip='top-end'
            onClick={(e) => { functionDownload() }}
            disabled={isDownloadContinue}
          >
            {!isDownloadContinue && (
              <KTSVG path='/media/icons/duotune/arrows/arr044.svg' className='svg-icon-3' />
            )}
            {isDownloadContinue && (
              <span className='indicator-progress' style={{ display: 'block' }}>
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
      </div>
      {/* end::Header */}

      {datas.totalCount === -1 &&
        <div className='card-body pt-2 pb-4 d-flex flex-wrap align-items-center'>
          <div className='d-flex flex-center me-5 pt-2'>
            <span className='indicator-progress' style={{ display: 'block', margin: 'auto' }}>
              <span className='spinner-border spinner-border-lg align-middle ms-2'></span>
              <div style={{ minWidth: chartSize + 'px', minHeight: chartSize + 'px' }}></div>
            </span>
          </div>
        </div>
      }

      {datas.totalCount === 0 && !datas.items.some((item) => item.label && item.label !== '-') && (
        <div className='text-center p-11'>
          {warnings.map((warning, key) => (
            <Link key={key} to={warning.url}>
              <div>
                {warning.message}
                &nbsp;<KTSVG path='/media/icons/duotune/arrows/arr095.svg' className='svg-icon-2' />
              </div>
            </Link>
          ))}
          <div>
            <img
              className='mw-100 mh-200px'
              alt='metronic'
              style={{ transform: isWaringImageMirror ? 'scaleX(-1)' : 'scaleX(1)' }}
              src={toAbsoluteUrl('/media/illustrations/sketchy-1/1.png')}
            />
          </div>
        </div>
      )}

      {datas.totalCount !== -1 && (datas.totalCount !== 0 || datas.items.some((item) => item.label && item.label !== '-')) && (
        <div className='card-body py-0 scroll-y mh-450px maxh-md-600px'>
          <div className='tab-content'>
            <div className='tab-pane fade show active' id='kt_table_widget_5_tab_1'>
              <div className='table-responsive'>
                <table className='table table-row-dashed table-row-gray-200 align-middle gs-0 gy-4'>
                  <thead className='sticky-table-head'>
                    <tr className=''>
                      <th className={sorting.key === 'label' ? (sorting.ascending === true ? 'fw-bolder text-muted table-sort-asc' : 'fw-bolder text-muted table-sort-desc') : 'fw-bolder text-muted'}
                        style={{ cursor: 'pointer', backgroundColor: (mode === 'light' ? '#fff' : '#1e1e2d') }} onClick={() => applySorting('label')}>{tableName}</th>
                      <th className={sorting.key === 'competitor' ? (sorting.ascending === true ? 'fw-bolder text-muted table-sort-asc' : 'fw-bolder text-muted table-sort-desc') : 'fw-bolder text-muted'}
                        style={{ cursor: 'pointer', backgroundColor: (mode === 'light' ? '#fff' : '#1e1e2d') }} onClick={() => applySorting('competitor')}>{LanguageHelp('Competitor')}</th>
                      {columnCount >= 1 && <th className={sorting.key === 'firstCount' ? (sorting.ascending === true ? 'fw-bolder text-muted table-sort-asc' : 'fw-bolder text-muted table-sort-desc') : 'fw-bolder text-muted'}
                        style={{ cursor: 'pointer', backgroundColor: (mode === 'light' ? '#fff' : '#1e1e2d') }} onClick={() => applySorting('firstCount')}>{header1}</th>}
                      <th className='fw-bolder text-muted' style={{ backgroundColor: (mode === 'light' ? '#fff' : '#1e1e2d') }}>{LanguageHelp('Happiness Score')} %</th>
                      {columnCount >= 2 && <th className={sorting.key === 'secondCount' ? (sorting.ascending === true ? 'fw-bolder text-muted table-sort-asc' : 'fw-bolder text-muted table-sort-desc') : 'fw-bolder text-muted'}
                        style={{ cursor: 'pointer', backgroundColor: (mode === 'light' ? '#fff' : '#1e1e2d') }} onClick={() => applySorting('secondCount')}>{header2}</th>}
                      {columnCount >= 3 && <th className={sorting.key === 'thirdCount' ? (sorting.ascending === true ? 'fw-bolder text-muted table-sort-asc' : 'fw-bolder text-muted table-sort-desc') : 'fw-bolder text-muted'}
                        style={{ cursor: 'pointer', backgroundColor: (mode === 'light' ? '#fff' : '#1e1e2d') }} onClick={() => applySorting('thirdCount')}>{header3}</th>}
                      {columnCount >= 4 && <th className={sorting.key === 'fourthCount' ? (sorting.ascending === true ? 'fw-bolder text-muted table-sort-asc' : 'fw-bolder text-muted table-sort-desc') : 'fw-bolder text-muted'}
                        style={{ cursor: 'pointer', backgroundColor: (mode === 'light' ? '#fff' : '#1e1e2d') }} onClick={() => applySorting('fourthCount')}>{header4}</th>}
                      {columnCount >= 5 && <th className={sorting.key === 'fifthCount' ? (sorting.ascending === true ? 'fw-bolder text-muted table-sort-asc' : 'fw-bolder text-muted table-sort-desc') : 'fw-bolder text-muted'}
                        style={{ cursor: 'pointer', backgroundColor: (mode === 'light' ? '#fff' : '#1e1e2d') }} onClick={() => applySorting('fifthCount')}>{header5}</th>}
                      {columnCount >= 6 && <th className={sorting.key === 'sixthCount' ? (sorting.ascending === true ? 'fw-bolder text-muted table-sort-asc' : 'fw-bolder text-muted table-sort-desc') : 'fw-bolder text-muted'}
                        style={{ cursor: 'pointer', backgroundColor: (mode === 'light' ? '#fff' : '#1e1e2d') }} onClick={() => applySorting('sixthCount')}>{header6}</th>}
                      <th className='' style={{ backgroundColor: (mode === 'light' ? '#fff' : '#1e1e2d') }}></th>
                    </tr>
                  </thead>

                  <tbody>
                    {paginatedItems.map((data, index) => (
                      <tr key={uniqueKey + '_' + index}>
                        <td>
                          <div className='d-flex align-items-center'>
                            <div className='d-flex flex-column'>
                              <span>{data.label}</span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className='d-flex align-items-center'>
                            <div className='d-flex flex-column'>
                              <span>{data.competitor}</span>
                            </div>
                          </div>
                        </td>
                        {columnCount >= 1 &&
                          <td className='text-dark fw-bold mb-1'>
                            {data.firstCount}
                            <Rating rate={data.firstRate} />
                          </td>
                        }
                        {columnCount >= 1 && columnCount >= 2 &&
                          <td className='text-dark fw-bold mb-1'>
                            {getHappinessScore(data.secondCount, data.thirdCount)}
                          </td>
                        }
                        {columnCount >= 2 &&
                          <td className='text-dark fw-bold mb-1'>
                            {data.secondCount}
                            <Rating rate={data.secondRate} />
                          </td>
                        }
                        {columnCount >= 3 &&
                          <td className='text-dark fw-bold mb-1'>
                            {data.thirdCount}
                            <Rating rate={data.thirdRate} reversed={true} />
                          </td>
                        }
                        {columnCount >= 4 &&
                          <td className='text-dark fw-bold mb-1'>
                            {data.fourthCount}
                            <Rating rate={data.fourthRate} />
                          </td>
                        }
                        {columnCount >= 5 &&
                          <td className='text-dark fw-bold mb-1'>
                            {data.fifthCount}
                            <Rating rate={data.fifthRate} />
                          </td>
                        }
                        {columnCount >= 6 &&
                          <td className='text-dark fw-bold mb-1'>
                            {data.sixthCount}
                            <Rating rate={data.sixthRate} />
                          </td>
                        }

                        {url !== "" &&
                          <td className='text-end'>
                            <a
                              href={
                                url +
                                (uniqueKey == 'subjectGroupCompetitionsForTable' ? 'filter_subjectgroup=' : 'filter_subject=') +
                                data.id +
                                '&filter_date=' +
                                filter_Date +
                                '&filter_channeltype=' +
                                filter_ChannelType +
                                '&filter_sourcetype=' +
                                filter_SourceType
                              }
                              className='btn btn-sm btn-icon btn-bg-light btn-active-color-primary'
                            >
                              <KTSVG path='/media/icons/duotune/arrows/arr064.svg' className='svg-icon-2' />
                            </a>
                          </td>
                        }
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export { TablesWidget15 }
